/**
 * Which of an idea's ingredients the active grocery list already has, by ingredient id.
 *
 * `linked` is an item on the list for this very ingredient: adding the idea again skips it, so it is
 * not offered for picking. `elsewhere` describes an item of the same thing that is on the list for
 * something else (other ideas, or typed in by hand): adding the idea links it to that item rather than
 * giving a second one, so it is only pointed out. The same thing is the same grocery item when both
 * sides say which, and the same name otherwise. Checked-off and struck items have been bought, so they
 * count as neither.
 *
 * @param {Array<{id: number, name: string, grocery_item_id?: number}>} ingredients
 * @param {Array<Object>|null|undefined} activeRows  the active list's rows, as selectActiveListRows() gives them
 * @param {Object<number, {name: string}>} ideasById  to say which ideas an `elsewhere` item is from
 * @returns {Map<number, {linked: boolean, elsewhere: string|null}>}  only ingredients on the list
 */
export default function ingredientsOnList(ingredients, activeRows, ideasById = {}) {
	const found = new Map();
	if(!activeRows) {
		return found;
	}
	const items = activeRows.filter((item) => item.name && !item.checked_off && !item.struck);
	const linked = new Set(items
		.flatMap((item) => item.meal_ideas || [])
		.filter((link) => link.meal_idea_ingredient_id !== null && link.meal_idea_ingredient_id !== undefined)
		.map((link) => Number(link.meal_idea_ingredient_id)));
	const key = (name) => name.trim().toLowerCase();
	const isSameThing = (item, ingredient) => (item.grocery_item_id && ingredient.grocery_item_id
		? Number(item.grocery_item_id) === Number(ingredient.grocery_item_id)
		: key(item.name) === key(ingredient.name));

	for(const ingredient of ingredients) {
		if(linked.has(Number(ingredient.id))) {
			found.set(ingredient.id, { linked: true, elsewhere: null });
			continue;
		}
		const same = items.find((item) => isSameThing(item, ingredient));
		if(same) {
			const names = [...new Set((same.meal_ideas || [])
				.map((link) => ideasById[link.meal_idea_id])
				.filter(Boolean)
				.map((idea) => idea.name))];
			found.set(ingredient.id, { linked: false, elsewhere: names.length ? `from ${names.join(', ')}` : 'added by hand' });
		}
	}
	return found;
}
